/**
 * Builds the work-periods payload sent to the API at submission time.
 *
 * Extracted from the old monolithic `onSubmit`. The source values carry optional
 * fields inherited from the Formik shape; at submission time we know (because the
 * form validated) that each period has `start.date` and `end.date`. This helper
 * performs a deep clone, filters out periods outside the mission window and
 * projects to the API payload shape.
 */
import { addDays, isWithinInterval, parseISO } from 'date-fns'
import { cloneDeep } from 'lodash'
import { WorkersRequestCreateUpdateFormValues } from '../workers-request-form.types'

/**
 * Shape produced for the API: each work period is reduced to its start/end dates.
 */
export type WorkPeriodsPayload = Array<{
  start: { date: Date }
  end: { date: Date }
}>

/**
 * Transforms the Formik work-periods into the API payload.
 *
 * @param values - Validated Formik values.
 * @returns Array of `{ start, end }` pairs filtered by the mission window.
 */
export const buildWorkPeriodsPayload = (
  values: WorkersRequestCreateUpdateFormValues,
): WorkPeriodsPayload => {
  const { startDate, endDate } = values.meta
  const missionStartDate = parseISO(startDate)
  // end is inclusive: we bump by 1 day so `isWithinInterval` keeps the last day.
  const missionEndDate = addDays(parseISO(endDate), 1)

  return (
    cloneDeep(
      values.missionData.workPeriods as Array<
        Required<WorkersRequestCreateUpdateFormValues['missionData']['workPeriods'][number]>
      >,
    )
      // Drop periods that fall outside the mission window.
      .filter(wP =>
        isWithinInterval(wP.start.date, { start: missionStartDate, end: missionEndDate }),
      )
      // Only the date fields are kept on the payload.
      .map(wP => ({
        start: { date: wP.start.date },
        end: { date: wP.end.date },
      }))
  )
}
